import type { Card, Finish } from './collection.ts';
import { COLOR_GROUPS, colorGroup, parseTypeLine, priceFor, TYPE_GROUPS, typeGroup } from './filing.ts';

// How a deck list is laid out: grouping, sorting, the filter box and the
// stats strip. Used by the deck page and the shared deck page.

export const DECK_VIEWS = ['list', 'stacks', 'grid'] as const;
export type DeckViewStyle = (typeof DECK_VIEWS)[number];

export const DECK_GROUPINGS = ['type', 'manaValue', 'color', 'none'] as const;
export type DeckGrouping = (typeof DECK_GROUPINGS)[number];

export const DECK_SORTS = ['name', 'manaValue', 'price'] as const;
export type DeckSort = (typeof DECK_SORTS)[number];

export type DeckItem = { card: Card; finish: Finish; quantity: number };

export type DeckSection<T extends DeckItem> = { title: string; items: T[] };

/** Type sections for a deck: spells first, lands last, as deck lists are usually read. */
const DECK_TYPE_ORDER = [...TYPE_GROUPS.filter((g) => g !== 'Lands'), 'Lands'];

const isLand = (card: Pick<Card, 'typeLine'>) => parseTypeLine(card.typeLine).types.includes('Land');

/** Mana value bucket: 0 to 6, then 7+. Lands get their own. */
function manaValueGroup(card: Pick<Card, 'typeLine' | 'cmc'>): string {
  if (isLand(card)) return 'Lands';
  const mv = Math.floor(card.cmc);
  return mv >= 7 ? '7+' : String(mv);
}
const MANA_VALUE_GROUPS = ['0', '1', '2', '3', '4', '5', '6', '7+', 'Lands'];

const byName = (a: DeckItem, b: DeckItem) =>
  a.card.name.localeCompare(b.card.name) || a.card.setCode.localeCompare(b.card.setCode);

const SORTS: Record<DeckSort, (a: DeckItem, b: DeckItem) => number> = {
  name: byName,
  manaValue: (a, b) => a.card.cmc - b.card.cmc || byName(a, b),
  // Most valuable first; cards with no price last.
  price: (a, b) => (priceFor(b.card, b.finish) ?? -1) - (priceFor(a.card, a.finish) ?? -1) || byName(a, b),
};

/**
 * The deck in sections: the commanders first, whatever the grouping, then the
 * rest grouped and sorted. Empty sections are left out.
 */
export function groupDeck<T extends DeckItem>(
  items: T[],
  { groupBy, sort, commanders = [] }: { groupBy: DeckGrouping; sort: DeckSort; commanders?: string[] },
): DeckSection<T>[] {
  const isCommander = (i: T) => commanders.includes(i.card.scryfallId);
  const sorted = [...items].sort(SORTS[sort]);
  const rest = sorted.filter((i) => !isCommander(i));

  const groups: [string, (i: T) => boolean][] =
    groupBy === 'type'
      ? DECK_TYPE_ORDER.map((g) => [g, (i) => typeGroup(i.card.typeLine) === g])
      : groupBy === 'manaValue'
        ? MANA_VALUE_GROUPS.map((g) => [g === 'Lands' ? g : `Mana value ${g}`, (i) => manaValueGroup(i.card) === g])
        : groupBy === 'color'
          ? COLOR_GROUPS.map((g) => [g, (i) => colorGroup(i.card) === g])
          : [['Cards', () => true]];

  const commanderItems = sorted.filter(isCommander);
  return [
    { title: commanderItems.length > 1 ? 'Commanders' : 'Commander', items: commanderItems },
    ...groups.map(([title, test]) => ({ title, items: rest.filter(test) })),
  ].filter((s) => s.items.length > 0);
}

/**
 * The filter box: every word must appear in the card's name, type line or
 * rules text ("draw creature" finds creatures that draw cards), or in
 * `extra`, such as the copy's foil type ("surge").
 */
export function matchesFilter(card: Pick<Card, 'name' | 'typeLine' | 'oracleText'>, query: string, extra = ''): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = `${card.name}\n${card.typeLine}\n${card.oracleText}\n${extra}`.toLowerCase();
  return words.every((w) => text.includes(w));
}

/** "{2}{G}{G} // {1}{U}" → [["2","G","G"],["1","U"]]: the symbols of each face. */
export function parseManaCost(cost: string | null): string[][] {
  if (!cost) return [];
  return cost.split(' // ').map((face) => [...face.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]!));
}

export const MANA_COLORS = ['W', 'U', 'B', 'R', 'G'] as const;
type ManaColor = (typeof MANA_COLORS)[number];

export type DeckStats = {
  cards: number;
  /** Cards per type group, lands last, empty groups left out. */
  types: { group: string; count: number }[];
  /** Non-land cards per mana value, 0 to 7+. */
  curve: { manaValue: string; count: number }[];
  /** Average mana value of the non-land cards. */
  averageManaValue: number;
  /** Per color: colored symbols in mana costs, and lands that can make it. */
  colors: { color: ManaColor; pips: number; sources: number }[];
};

export function deckStats(items: DeckItem[]): DeckStats {
  let cards = 0;
  let spells = 0;
  let manaValueTotal = 0;
  const types = new Map<string, number>();
  const curve = new Map<string, number>(MANA_VALUE_GROUPS.slice(0, -1).map((g) => [g, 0]));
  const pips = new Map<ManaColor, number>(MANA_COLORS.map((c) => [c, 0]));
  const sources = new Map<ManaColor, number>(MANA_COLORS.map((c) => [c, 0]));

  for (const { card, quantity } of items) {
    cards += quantity;
    const group = typeGroup(card.typeLine);
    types.set(group, (types.get(group) ?? 0) + quantity);
    if (isLand(card)) {
      for (const c of MANA_COLORS) if (card.producedMana.includes(c)) sources.set(c, sources.get(c)! + quantity);
      continue;
    }
    spells += quantity;
    manaValueTotal += card.cmc * quantity;
    const mv = manaValueGroup(card);
    curve.set(mv, curve.get(mv)! + quantity);
    // A hybrid or Phyrexian symbol counts toward each of its colors.
    for (const symbol of parseManaCost(card.manaCost).flat()) {
      for (const c of MANA_COLORS) if (symbol.split('/').includes(c)) pips.set(c, pips.get(c)! + quantity);
    }
  }

  return {
    cards,
    types: DECK_TYPE_ORDER.filter((g) => types.has(g)).map((group) => ({ group, count: types.get(group)! })),
    curve: [...curve].map(([manaValue, count]) => ({ manaValue, count })),
    averageManaValue: spells ? manaValueTotal / spells : 0,
    colors: MANA_COLORS.map((color) => ({ color, pips: pips.get(color)!, sources: sources.get(color)! })).filter(
      (c) => c.pips > 0 || c.sources > 0,
    ),
  };
}
